import { EventBus } from '@/helpers/event-bus';
import { showLoading, hideLoading, isLoadingVisible } from '@/mixins/ui-mixin';
import { actions as _act } from '@/models/store.actions';
import { showSignWithQrCodeDialog } from '@/plugins/dialogs';
import { diiaSignService } from '@/services/diia-sign.service';
import { AppSignKeyOperationType, CloudProvider, PrivateKeyType, SignPurpose } from '@/sign/models/sign-constants';
import store from '@/store/store';
import { getRecaptchaTokenIfNotAuthenticated } from '@/helpers/recaptha-helper';

interface IDiiaSignResultSubscriber {
  onSignResult: (signResult: IDiiaSignResult) => void;
  onSignError: (error: Error) => void;
}

interface IDiiaSignResultGetStrategy {
  execute: (requestId: string, callback: (result: any) => void) => void;
  stop: () => void;
  getSignResponseFromApi: () => Promise<IDiiaSignResponse>;
}

interface IDiiaSignRequestCreationResult {
  requestId: string;
  deepLink: string;
}

interface IDiiaSignResponse {
  isSuccess: boolean;
  isError: boolean;
  error: string;
  signResult: IDiiaSignResult;
}

interface IDiiaSignResult {
  signedItems: Array<IDiiaSignedFile>;
}

interface IDiiaSignedFile {
  name: string;
  signature: string;
}

interface IDiiaUserSettings {
  qrCodeLifeTimeSeconds: number;
  useSignalR: boolean;
  maxWaitingTimeForSigningMinutes: number;
}

function getDiiaUserSettings(): IDiiaUserSettings {
  let appSettings = store.getters[_act.SETT_GET_APPSET];
  return appSettings?.diia as IDiiaUserSettings;
}

export class DiiaSignExecutor implements IDiiaSignResultSubscriber {
  private qrCodeLifeTimeSeconds: number = 0;

  private signPromiseResolver = (any) => { };
  private signPromiseRejecter = (any) => { };
  private hashesToSign: Array<any>;
  private signPurpose: SignPurpose;
  private deepLink: string = '';
  private requestId: string = '';
  private isDiiaSignDlgCreated: boolean = false;
  private signResultWaiter: DiiaSignResultWaiter;
  private isLoadingVisible: boolean = false;
  private isSeal: boolean = false;

  constructor(hashes: Array<any>, signPurpose: SignPurpose, isSeal: boolean = false) {
    this.hashesToSign = hashes;
    this.signPurpose = signPurpose;
    this.isSeal = isSeal;

    this.subscribe();
    this.signResultWaiter = new DiiaSignResultWaiter(this);
    let diiaSettings = getDiiaUserSettings();
    this.qrCodeLifeTimeSeconds = diiaSettings.qrCodeLifeTimeSeconds;
  }

  public destroy() {
    this.signResultWaiter.stop();
    this.unsubscribe();
  }

  private subscribe() {
    EventBus.$on('app-sign-with-qrcode-dlg-cancelled', this.cancelSigning.bind(this));
    EventBus.$on('app-sign-with-qrcode-dlg-refresh-qr-code', this.restartSigning.bind(this));
  }

  private unsubscribe() {
    EventBus.$off('app-sign-with-qrcode-dlg-cancelled');
    EventBus.$off('app-sign-with-qrcode-dlg-refresh-qr-code');
  }

  public sign(): Promise<any> {
    this.isLoadingVisible = isLoadingVisible();

    this.beginSigning();

    return new Promise((resolve, reject) => {
      this.signPromiseResolver = resolve;
      this.signPromiseRejecter = reject;
    });
  }

  private async beginSigning() {
    try {
      await this.getDeepLink();

      this.showQrCode();

      this.signResultWaiter.start(this.requestId);
    } catch (err) {
      this.onSignError(err);
    }
  }

  private async cancelSigning() {
    try {
      await this.stopSigning();

      this.hideDiiaSignDlg();

      this.signPromiseRejecter(new Error('cancelled'));
    } catch (err) {
      this.onSignError(err);
    }
  }

  private async restartSigning() {
    try {
      await this.stopSigning();

      this.beginSigning();
    } catch (err) {
      this.onSignError(err);
    }
  }

  private async stopSigning() {
    this.signResultWaiter.stop();

    showLoading({}, true);

    await diiaSignService.cancelSigning(this.requestId);

    hideLoading();
  }

  private async getDeepLink() {
    try {
      showLoading({}, true);

      let { recaptchaToken, recaptchaType } = await getRecaptchaTokenIfNotAuthenticated();

      let signRequestResult = (await diiaSignService.sign(this.hashesToSign, this.signPurpose,
        recaptchaToken, recaptchaType)) as IDiiaSignRequestCreationResult;

      this.requestId = signRequestResult.requestId;
      this.deepLink = signRequestResult.deepLink;
    } finally {
      hideLoading();
    }
  }

  private showQrCode() {
    const opts = {
      deepLink: this.deepLink,
      deepLinkLifeTimeSeconds: this.qrCodeLifeTimeSeconds,
      cloudProvider: CloudProvider.DPDiya,
      privateKeyType: this.isSeal ? PrivateKeyType.Seal : PrivateKeyType.Key,
      keyOperationType: AppSignKeyOperationType.Signing
    };
    if (this.isDiiaSignDlgCreated) {
      EventBus.$emit('app-sign-with-qrcode-dlg-new-qr-code', opts);
    } else {
      showSignWithQrCodeDialog(opts);
      this.isDiiaSignDlgCreated = true;
    }
  }

  private hideDiiaSignDlg() {
    EventBus.$emit('app-sign-with-qrcode-dlg-close');
  }

  onSignResult(signResult: IDiiaSignResult) {
    this.onSigningEnd();
    this.signPromiseResolver(signResult.signedItems);
  }

  onSignError(err) {
    this.onSigningEnd();
    this.signPromiseRejecter(err);
  }

  private onSigningEnd() {
    this.hideDiiaSignDlg();
    if (this.isLoadingVisible) {
      showLoading({}, true);
    }
  }
}

class DiiaSignResultWaiter {
  private maxTimeToWaitSigningMinutes: number = 0;
  private isUseSignalR: boolean = false;

  private requestId: string = '';
  private executing: boolean = false;
  private strategy?: IDiiaSignResultGetStrategy;
  private subscriber: IDiiaSignResultSubscriber;
  private signRequestExpirationTimeout: any;

  private get isSignalRConnected(): boolean {
    return store.getters[_act.SETTTEMP_GET_SIGNALRCONNECTED];
  }

  constructor(subscriber: IDiiaSignResultSubscriber) {
    this.subscriber = subscriber;
    this.subscribe();
    let diiaSettings = getDiiaUserSettings();
    this.isUseSignalR = diiaSettings.useSignalR;
    this.maxTimeToWaitSigningMinutes = diiaSettings.maxWaitingTimeForSigningMinutes;
  }

  start(requestId: string) {
    if (this.executing) {
      this.stop();
    }

    this.requestId = requestId;

    if (this.requestId && this.subscriber) {
      this.executing = true;

      this.signRequestExpirationTimeout = setTimeout(async() => {
        if (this.executing) {
          this.strategy?.stop();
          let signResult = await this.strategy?.getSignResponseFromApi();
          this.onStrategyExecuted(signResult || {} as IDiiaSignResponse, new Error('Signing timeout'));
        }
      }, this.maxTimeToWaitSigningMinutes * 60 * 1000);

      this.strategy = this.getStrategy();
      this.strategy?.execute(this.requestId, this.onStrategyExecuted.bind(this));
    }
  }

  async stop() {
    this.executing = false;
    clearTimeout(this.signRequestExpirationTimeout);
    this.strategy?.stop();
    this.unsubscribe();
  }

  private getStrategy(): IDiiaSignResultGetStrategy {
    return this.isUseSignalR && this.isSignalRConnected
      ? new DiiaSignResultGetSignalRStrategy()
      : new DiiaSignResultGetApiCallStrategy();
  }

  private onStrategyExecuted(result: IDiiaSignResponse, unknownError?: Error) {
    if (this.executing) {
      this.executing = false;
      if (result && result.isSuccess) {
        this.subscriber?.onSignResult(result.signResult);
      } else {
        let err;
        if (result) {
          if (result.error) {
            err = new Error(result.error);
          } else {
            err = unknownError || new Error('Unknown error');
          }
        } else {
          err = new Error('Signing timeout');
        }
        this.subscriber?.onSignError(err);
      }
    }
  }

  private subscribe() {
    if (this.isUseSignalR) {
      EventBus.$on('signalr-connection-state-changed', this.signalRConnectionStateChanged.bind(this));
    }
  }

  private unsubscribe() {
    EventBus.$off('signalr-connection-state-changed');
  }

  private signalRConnectionStateChanged() {
    if (this.executing &&
      this.strategy instanceof DiiaSignResultGetSignalRStrategy &&
      !this.isSignalRConnected) {
      this.strategy.stop();
      this.strategy = new DiiaSignResultGetApiCallStrategy();
      this.strategy.execute(this.requestId, this.onStrategyExecuted.bind(this));
    }
  }
}

abstract class DiiaSignResultGetBaseStrategy implements IDiiaSignResultGetStrategy {
  protected requestId: string = '';
  protected callback?: (result: any) => void;
  protected isStopped: boolean = false;

  execute(requestId: string, callback: (result: any) => void) {
    this.requestId = requestId;
    this.callback = callback;
  }

  stop() {
    this.isStopped = true;
  }

  public async getSignResponseFromApi(): Promise<IDiiaSignResponse> {
    let signResult = await diiaSignService.getSignResponse(this.requestId);
    return signResult;
  }
}

class DiiaSignResultGetApiCallStrategy extends DiiaSignResultGetBaseStrategy {
  private apiCallFrequencySeconds = 5;
  private timeout: any;

  execute(requestId: string, callback: (result: any) => void) {
    super.execute(requestId, callback);

    this.deferGetSignResult();
  }

  stop() {
    super.stop();
    clearTimeout(this.timeout);
  }

  private deferGetSignResult() {
    this.timeout = setTimeout(this.getSignResult.bind(this), this.apiCallFrequencySeconds * 1000);
  }

  private async getSignResult() {
    let signResponse;
    try {
      signResponse = await this.getSignResponseFromApi();
    } catch (error) {
      console.error(error);
      signResponse = {};
    }

    if (!this.isStopped) {
      if (!signResponse || signResponse.isSuccess || signResponse.isError) {
        this.stop();
        this.callback?.(signResponse);
      } else {
        this.deferGetSignResult();
      }
    }
  }
}

class DiiaSignResultGetSignalRStrategy extends DiiaSignResultGetBaseStrategy {
  execute(requestId: string, callback: (result: any) => void) {
    super.execute(requestId, callback);

    EventBus.$on('app-sign-diia-user-got-result', this.onGotResult.bind(this));
  }

  stop() {
    super.stop();
    EventBus.$off('app-sign-diia-user-got-result');
  }

  private async onGotResult(data) {
    if (!this.isStopped && data && data.requestId === this.requestId) {
      let signResponse = await this.getSignResponseFromApi();
      if (!this.isStopped) {
        this.stop();
        this.callback?.(signResponse);
      }
    }
  }
}
